import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, MapPin } from "lucide-react";
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const AdvancePayment = () => {
  const [formData, setFormData] = useState({
    serialNo: "",
    landOwnerName: "",
    gatNumber: "",
    totalArea: "",
    amount: "",
    paymentDate: "",
  });
  const [apiRecords, setApiRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [currentLaoId, setCurrentLaoId] = useState(null);
  const [records, setRecords] = useState([]);
  const [proposalDetails, setProposalDetails] = useState(null);
  const { language, translations } = useLanguage();

  // Location filter states
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');

  // Filter states
  const [filterMode, setFilterMode] = useState('all');

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  //Proposal ID
  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');

      if (currentProposal) {
        try {
          const proposalId = currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null;

          const laoUserId = currentProposal?.data?.usr_id || currentProposal?.usr_id;

          setCurrentLaoId(laoUserId);
          console.log("Current LAO User ID:", laoUserId);

          return { proposalId };
        } catch (e) {
          console.error('Error parsing current project data (getProposalInfo):', e);
        }
      }
      return { proposalId: null };
    };

    const proposalInfo = getProposalInfo();
    setCurrentProposalId(proposalInfo.proposalId);
    console.log('Current Project-Proposal Info:', proposalInfo);
  }, []);

  // Get selected project from localStorage
  useEffect(() => {
    const getSelectedProject = () => {
      try {
        const storedProject = localStorage.getItem('selectedProject');
        if (storedProject) {
          const project = JSON.parse(storedProject);
          const projectIdFromStorage = project.id || project.project_id;
          const projectName = project.project_name || '';

          if (projectIdFromStorage && projectIdFromStorage !== currentProjectId) {
            setCurrentProjectId(projectIdFromStorage);
            setCurrentProjectName(projectName);
          }
        }
      } catch (error) {
        console.error('Error reading selected project:', error);
      }
    };

    getSelectedProject();
    window.addEventListener('storage', getSelectedProject);
    return () => window.removeEventListener('storage', getSelectedProject);
  }, [currentProjectId]);

  // Fetch advance payment data for all locations
  const fetchAdvancePaymentsForAll = async () => {
    if (!currentProjectId || !currentProposalId) {
      setError('Project ID and Proposal ID are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get('/getAdvancePayment', {
        projectId: currentProjectId,
        proposalId: currentProposalId
      });

      if (response.status === 200 && response.data) {
        setApiRecords(response.data);
        setFilteredRecords(response.data);
      } else {
        // setError('Failed to fetch advance payment data');
        setApiRecords([]);
        setFilteredRecords([]);
      }
    } catch (err) {
      console.error('Error fetching advance payments:', err);
      setError('Error fetching advance payment data: ' + err.message);
      setApiRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data based on filter mode
  useEffect(() => {
    if (filterMode === 'all' && currentProjectId && currentProposalId) {
      fetchAdvancePaymentsForAll();
    }
  }, [filterMode, currentProjectId, currentProposalId]);


  // Helper functions
  const hasValidLocationSelection = selectedDistrict;
  const canShowData = filterMode === 'all' || (filterMode === 'location' && hasValidLocationSelection);

  // Group records by village
  const groupedRecords = filteredRecords.reduce((acc, record) => {
    const village = record.village_name || 'Other';
    if (!acc[village]) {
      acc[village] = [];
    }
    acc[village].push(record);
    return acc;
  }, {});

  // Format date function
  const formatDateMarathi = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
    <html>
    <head>
      <meta charset="UTF-8">
      <title>आगाऊ मोबदला रक्कम</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet">
      <style>
        @page {
          size: A4 portrait;
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
          font-size: 12pt;
          line-height: 1.4;
        }
        
        * {
          box-sizing: border-box;
        }
        
        h1, h2, h3, h4, h5, h6 {
          margin: 1rem 0 0.5rem 0;
          page-break-after: avoid;
          font-weight: bold;
        }
        
        h2 {
          font-size: 16pt;
          text-align: center;
          margin-top: 1rem;
          margin-bottom: 1rem;
          font-family: 'DVOT-Surekh', 'Mangal', sans-serif;
        }
        
        h3 {
          text-align: left;
          margin: 15px 0 5px 0;
          font-size: 13pt;
          background-color: #f8f9fa;
          padding: 5px;
          border-left: 4px solid #000;
        }
        
        p {
          margin: 0.5rem 0;
          line-height: 1.6;
          orphans: 3;
          widows: 3;
          font-size: 11pt;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 5px;
          margin-bottom: 1.5rem;
          page-break-inside: auto !important;
          font-size: 10pt;
        }
        
        thead {
          display: table-header-group;
        }
        
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        
        th, td {
          border: 1px solid #000;
          padding: 4px;
          text-align: center;
          font-size: 9pt;
          vertical-align: middle;
          page-break-inside: avoid;
        }
        
        th {
          background-color: #f0f0f0;
          font-weight: bold;
          font-size: 10pt;
        }

        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }
        .text-base { font-size: 11pt; }
        .text-lg { font-size: 1.125rem; }
        .font-bold { font-weight: bold; }
        
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page-break { page-break-after: always; }
          table { page-break-inside: auto !important; font-size: 10px; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          th, td { page-break-inside: avoid; page-break-after: auto; padding: 2px; }
        }
      </style>
    </head>
    <body>
    <h1 class="text-center font-bold text-lg">${currentProjectName}</h1>
    <h1 class="text-center font-bold text-base">भूसंपादन प्रकरण क्रमांक ${proposalDetails?.section_11_bhusampadan_no || ''}</h1>
      <h2 style="text-align: center;">${translations[language]?.AdAdvanceAmount || "अग्रिमाची रक्कम"}</h2>
      
      ${Object.entries(groupedRecords).map(([village, records]) => `
        <h3>गाव: ${village}</h3>
        <table>
          <thead>
            <tr>
              <th>${translations[language]?.AdSrNo || "अ.क्र."}</th>
              <th>${translations[language]?.AdLandholderName || "भूधारकाचे नाव"}</th>
              <th>${translations[language]?.AdSurveyNumber || "गट/सर्व्हे क्रमांक"}</th>
              <th>${translations[language]?.AdTotalArea || "एकूण क्षेत्र (हे.आर.)"}</th>
              <th>${translations[language]?.AdAdvanceAmount || "अग्रिमाची रक्कम"}</th>
              <th>${translations[language]?.AdSanctionDate || "प्रदानाचा दिनांक"}</th>
              <th>${translations[language]?.No || "मंजुरी आदेश क्रमांक"}</th>
              <th>${translations[language]?.AdDate || "मंजुरी आदेश दिनांक"}</th>
            </tr>
          </thead>
          <tbody>
            ${records.map((record, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${record.owner_name}</td>
                <td>${record.survey_no}</td>
                <td>${record.owner_total_area || "-"}</td>
                <td>${record.amount ? `₹${record.amount}` : '-'}</td>
                <td>${record.date ? formatDate(record.date) : '-'}</td>
                <td>${record.approval_no || '-'}</td>
                <td>${record.approval_date ? formatDate(record.approval_date) : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `).join('')}
      
    </body>
    </html>
  `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Start editing
  const startEdit = (record) => {
    setEditingId(record.land_owner_id);
    setEditData({
      amount: record.amount || '',
      date: record.date ? new Date(record.date).toISOString().split('T')[0] : ''
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  // Handle edit input change
  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save edited record
  const saveEdit = async (record) => {
    if (!editData.amount || !editData.date) {
      alert('कृपया रक्कम आणि दिनांक भरा');
      return;
    }

    try {
      setLoading(true);

      const updateData = {
        project_id: currentProjectId,
        proposal_id: currentProposalId,
        owner_id: record.land_owner_id,
        amount: editData.amount,
        date: editData.date,
        approval_no: editData.approval_no,
        approval_date: editData.approval_date,
      };

      // Use the single API endpoint for both create and update
      await apiService.post('/postAddUpdateAdvancePayment', updateData);

      // Refresh data after successful update
      if (filterMode === 'all') {
        await fetchAdvancePaymentsForAll();
      }

      setEditingId(null);
      setEditData({});

      alert('✅ डेटा यशस्वीपणे अपडेट झाला');
    } catch (err) {
      console.error('Error updating record:', err);
      alert('❌ डेटा अपडेट करतांना त्रुटी: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="flex justify-center items-center h-80">
          <div className="text-center">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">डेटा लोड होत आहे...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Search and Actions Bar */}
        {canShowData && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">

              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* Print Button */}
                <button
                  onClick={handlePrint}
                  disabled={filteredRecords.length === 0}
                  className="flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 shadow-lg transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {translations[language]?.Print || 'Print'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Table Grouped by Village */}
        {canShowData && filteredRecords.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            {Object.entries(groupedRecords).map(([village, villageRecords]) => (
              <div key={village} className="mb-10 last:mb-0 border rounded-xl overflow-hidden shadow-sm">
                
                {/* Village Header Header */}
                <div className="bg-indigo-50 border-b p-4 flex items-center">
                  <MapPin className="h-5 w-5 text-indigo-500 mr-2" />
                  <h3 className="text-lg font-bold text-gray-800">
                    {translations[language]?.AdVillage || 'गाव'}: {village}
                  </h3>
                  <span className="ml-auto bg-white px-3 py-1 rounded-full text-sm font-semibold text-indigo-600 border border-indigo-100">
                    Total Records: {villageRecords.length}
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border-t-0 border-gray-300 text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                          <th className="border p-3">{translations[language]?.AdAction}</th>
                        )}
                        <th className="border p-3">{translations[language]?.AdSrNo}</th>
                        <th className="border p-3 text-left">{translations[language]?.AdLandholderName}</th>
                        <th className="border p-3">{translations[language]?.AdSurveyNumber}</th>
                        <th className="border p-3">{translations[language]?.AdTotalArea}</th>
                        <th className="border p-3 text-right">{translations[language]?.AdAdvanceAmount}</th>
                        <th className="border p-3">{translations[language]?.AdSanctionDate}</th>
                        <th className="border p-3">{translations[language]?.No}</th>
                        <th className="border p-3">{translations[language]?.AdApprovalDate}</th>
                      </tr>
                    </thead>
                    <tbody>

                      {villageRecords.map((record, index) => (
                        <tr key={record.land_owner_id} className="hover:bg-gray-50 transition-colors">
                          
                          {/* Data Entry Actions */}
                          {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                            <td className="border p-2 text-center">
                              {editingId === record.land_owner_id ? (
                                <div className="flex gap-2 justify-center">
                                  <button
                                    onClick={() => saveEdit(record)}
                                    disabled={loading}
                                    className="bg-green-500 text-white p-1.5 rounded hover:bg-green-600 shadow-sm disabled:opacity-50"
                                    title="सेव करा"
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 shadow-sm"
                                    title="रद्द करा"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEdit(record)}
                                  className="bg-blue-500 text-white p-1.5 rounded hover:bg-blue-600 shadow-sm"
                                  title="संपादित करा"
                                >
                                  <Edit2 size={16} />
                                </button>
                              )}
                            </td>
                          )}
                          
                          <td className="border p-2 text-center">{index + 1}</td>
                          {/* भूधारकाचे नाव  */}
                          <td className="border p-2 font-medium">{record.owner_name}</td>
                          {/* गट/सर्व्हे क्रमांक   */}
                          <td className="border p-2 text-center">{record.survey_no}</td>
                          {/* एकूण क्षेत्र (हे.आर.)  */}
                          <td className="border p-2 text-center">{record.owner_total_area || "-"} </td>
                          
                          {/* अग्रिमाची रक्कम  */}
                          <td className="border p-2 text-right">
                            {editingId === record.land_owner_id ? (
                              <input
                                type="number"
                                value={editData.amount}
                                onChange={(e) => handleEditChange('amount', e.target.value)}
                                className="w-full p-1.5 border rounded border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
                                placeholder="रक्कम"
                              />
                            ) : (
                              <span className={record.amount ? "font-semibold text-green-700" : ""}>
                                {record.amount ? `₹${record.amount}` : '-'}
                              </span>
                            )}
                          </td>
                          
                          {/* प्रदानाचा दिनांक   */}
                          <td className="border p-2 text-center">
                            {editingId === record.land_owner_id ? (
                              <input
                                type="date"
                                value={editData.date}
                                onChange={(e) => handleEditChange('date', e.target.value)}
                                className="w-full p-1.5 border rounded border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            ) : (
                              record.date ? formatDate(record.date) : '-'
                            )}
                          </td>

                          {/* तपशील  */}
                          <td className="border p-2 text-center">
                            {editingId === record.land_owner_id ? (
                              <input
                                type="text"
                                value={editData.approval_no || ''}
                                onChange={(e) => handleEditChange('approval_no', e.target.value)}
                                className="w-full p-1.5 border rounded border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            ) : (
                              record.approval_no ? record.approval_no : '-'
                            )}
                          </td>
                          
                          {/* मंजुरी आदेश दिनांक   */}
                          <td className="border p-2 text-center">
                            {editingId === record.land_owner_id ? (
                              <input
                                type="date"
                                value={editData.approval_date || ''}
                                onChange={(e) => handleEditChange('approval_date', e.target.value)}
                                className="w-full p-1.5 border rounded border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            ) : (
                              record.approval_date ? formatDate(record.approval_date) : '-'
                            )}
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Data Message */}
        {canShowData && filteredRecords.length === 0 && !loading && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <MapPin className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations[language]?.NoRecordsFound || 'No Records Found'}</h3>
            <p className="text-gray-600">कोणताही डेटा उपलब्ध नाही</p>
          </div>
        )}

        {/* Location Selection Required Message */}
        {filterMode === 'location' && !hasValidLocationSelection && (
          <div className="mt-6 text-center py-12">
            <div className="max-w-md mx-auto">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">{translations[language]?.LocationSelectionRequired || 'Location Selection Required'}</p>
              <p className="text-gray-400 text-sm">{translations[language]?.PleaseSelectFromFilter || 'Please select at least district from the filter panel above to view data.'}</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdvancePayment;